import {useCallback, useEffect, useState} from 'react';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {isServer} from 'coreUtils/utils';
import {useUI} from 'coreHooks';
import {useDebouncedCallback} from './useDebounce';

export interface IWindowSize {
	width: number;
	height: number;
}

type TCallback = (windowSize: IWindowSize) => void;

let isEventBind = false;
const callbacks: TCallback[] = [];

/**
 * Get the current dimensions of the window. ( Server version, mobile first )
 * If on the server, return default mobile dimensions. Because we don't know the actual dimensions.
 *
 * @returns {IWindowSize} Current window dimensions or default mobile dimensions.
 */
const getServerWindowObject = (): IWindowSize => {
	return {
		width: 414,
		height: 896,
	};
};

/**
 * Get the current dimensions of the window.
 * If on the server, return default mobile dimensions. Because we don't know the actual dimensions.
 *
 * @returns {IWindowSize} Current window dimensions or default mobile dimensions.
 */
export const getWindowObj = (): IWindowSize => {
	if (isServer) {
		return getServerWindowObject();
	}

	return {
		width: window.innerWidth,
		height: window.innerHeight,
	};
};

/**
 * Execute all registered resize callbacks with the current window dimensions.
 *
 * @param {IWindowSize} windowSize - Current window dimensions.
 * @returns {void}
 */
const updateCallbacks = (windowSize: IWindowSize) => {
	callbacks.forEach((c) => c(windowSize));
};

/**
 * Execute the updateCallbacks function whenever the window is resized.
 *
 * @returns {void}
 */
const callbacksUpdater = () => {
	updateCallbacks(getWindowObj());
};

/**
 * Register the callbackUpdater to listen for window resize events.
 *
 * @returns {void}
 */
const bindEvent = () => {
	window.addEventListener('resize', callbacksUpdater);
};

/**
 * Remove the callbackUpdater from the window's resize event listeners.
 *
 * @returns {void}
 */
const unBindEvent = () => {
	window.removeEventListener('resize', callbacksUpdater);
};

/**
 * Custom hook to handle window resize events.
 *
 * This hook will either update its local state with new window dimensions or
 * execute a provided callback function when the window is resized.
 *
 * @param {number} debounceTimeout - Delay time in ms to debounce the resize event. Default is 100ms.
 * @param {boolean} useBreakPoints - Flag to determine if breakpoints should be considered in the resize logic.
 * @param {TCallback} onResizeCallback - Optional external callback to be executed on resize instead of local state update.
 *
 * @returns {IWindowSize} Current window dimensions.
 */
const useWindowResize = (debounceTimeout = 100, useBreakPoints = false, onResizeCallback?: TCallback) => {
	let lastBreakPoint: string;
	const isServerUI = useUI('isServer');
	const [windowSize, setWindowSize] = useState<IWindowSize>(isServerUI ? getServerWindowObject() : getWindowObj());

	const handleResize = useCallback((windowSize: IWindowSize) => {
		// If an external callback is provided, it will be used instead of updating the local state.
		// This is useful for components that do not need to re-render on resize.
		if (typeof onResizeCallback === 'function') {
			onResizeCallback(windowSize);

			return;
		}

		const currentBreakPoint = ViewportMgr.getCurrentBreakpoint();

		if ((!useBreakPoints && windowSize.width !== windowSize.width) || lastBreakPoint !== currentBreakPoint) {
			setWindowSize(windowSize);
			lastBreakPoint = currentBreakPoint;
		}
	}, []);

	const debouncedHandleResize = useDebouncedCallback(handleResize, debounceTimeout);

	useEffect(() => {
		if (!isServer && !isEventBind) {
			bindEvent();
		}

		callbacks.push(debouncedHandleResize);
		lastBreakPoint = ViewportMgr.getCurrentBreakpoint();

		const windowObject = getWindowObj();

		if (windowSize.width !== windowObject.width || windowSize.height !== windowObject.height) {
			setWindowSize(windowObject);
		}

		return () => {
			// By iterating in reverse, we ensure that we're always checking items that haven't shifted due to removals.
			for (let index = callbacks.length - 1; index >= 0; index--) {
				const callback = callbacks[index];

				if (debouncedHandleResize === callback) {
					callbacks.splice(index, 1);
				}
			}

			if (callbacks.length === 0) {
				// If there are no callbacks left, unbind the event listener to avoid unnecessary checks.
				unBindEvent();
				isEventBind = false;
			}
		};
	}, []);

	return windowSize;
};

export default useWindowResize;
